import { useAppDispatch, useAppSelector } from '@store';
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  Checkbox,
  IconExpand,
  PermissionEnum,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { ApplicationDocumentsTypes } from './types';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import StepPartBlock from '../step-part-block';
import DocumentsControls from './parts/controls';
import DocumentRow from './parts/document-row';
import DocumentsPopups from './parts/popups';
import CreateApplicationDocument from './parts/create-document';
import { onSelectDocument } from '../../../../hoc/new-application/individual/store/slices';
import { onSelectDocument as onSelectCofDocument } from '../../../../hoc/new-application/main-representative/store/slices';
import { onSelectDocument as onSelectCorporateDocument } from '../../../../hoc/new-application/corporate/store/slices';
import useDocuments from './hooks/use-documents';

import cn from './styles.module.css';

const ApplicationDocuments = ({
  isCof,
  isCorporate,
  applicationStatus,
  zIndex,
  applicationId,
  documents,
  allowedDocumentsCategories,
  errors,
  clearErrors,
  search,
  renameAvailable,
}: ApplicationDocumentsTypes) => {
  const { selectedDocumentsCounts, deleteSuccess } = useAppSelector(
    (state) => state.newApplications.individualInfo,
  );
  const { selectedDocumentsCounts: cofSelectedDocumentsCount } = useAppSelector(
    (state) => state.newApplications.mainRepresentative,
  );
  const {
    selectedDocumentsCounts: corporateSelectedDocumentsCount,
    deleteSuccess: corporateDeleteSuccess,
  } = useAppSelector((state) => state.newApplications.corporateInfo);
  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    setPanelIsOpen,
    setMenuVisible,
    menuVisible,
    moveInProgress,
    changeDocumentCategoryAction,
    statusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeDocumentStatusAction,
    isLoading,
    getFiles,
    setDeletePopupVisible,
    onGroupSelect,
    setRenamePopupVisible,
    setSelectedDocument,
    onDeleteDocuments,
    deleteInProgress,
    deletePopupVisible,
    setDocumentName,
    setNameError,
    onRename,
    renameInProgress,
    renamePopupVisible,
    selectedDocument,
    documentName,
    nameError,
    renameErrors,
    panelIsOpen,
    handleCloseCreateDocumentSidepanel,
    documentSubmitInProgress,
    onSubmitDocuments,
    uploadInputRef,
    dropZoneInputRef,
    uploadedFiles,
    setUploadedFiles,
    uploadedFilesErrors,
    selectedGroup,
    onUpload,
    setSelectedGroup,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    onDelete,
    setExpirationDate,
    expirationDate,
    documentGroupError,
    attachedDocumentsErrors,
    attachedFiles,
    uploadedDocumentsErrors,
  } = useDocuments({
    isCof,
    isCorporate,
    applicationId,
    documents,
    deleteSuccess: isCorporate ? corporateDeleteSuccess : deleteSuccess,
  });

  const dispatch = useAppDispatch();

  const createButtonVisible =
    applicationStatus !== NewApplicationStatus.ACCEPTED &&
    applicationStatus !== NewApplicationStatus.DECLINED &&
    activePermissions?.includes(
      PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_CREATE_ACTION,
    );

  return (
    <StepPartBlock title="Company documents" zIndex={zIndex}>
      <div className={cn.documentsBlockHeader}>
        {createButtonVisible && (
          <ButtonSecondary
            text="Add document"
            icon={<IconExpand />}
            className={cn.addDocumentButton}
            size={ButtonSize.SMALL}
            onClick={() => setPanelIsOpen(true)}
            tabIndex={-1}
          />
        )}

        <DocumentsControls
          selectedDocumentsCounts={selectedDocumentsCounts}
          cofSelectedDocumentsCount={cofSelectedDocumentsCount}
          corporateSelectedDocumentsCount={corporateSelectedDocumentsCount}
          applicationStatus={applicationStatus}
          setMenuVisible={setMenuVisible}
          menuVisible={menuVisible}
          moveInProgress={moveInProgress}
          isCof={isCof}
          isCorporate={isCorporate}
          allowedDocumentsCategories={allowedDocumentsCategories}
          changeDocumentCategoryAction={changeDocumentCategoryAction}
          activePermissions={activePermissions}
          statusChangeInProgress={statusChangeInProgress}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          changeDocumentStatusAction={changeDocumentStatusAction}
          isLoading={isLoading}
          getFiles={getFiles}
          setDeletePopupVisible={setDeletePopupVisible}
        />
      </div>
      <div className={cn.documentsHeader}>
        <div className={cn.documentsHeaderRow}>
          <Checkbox
            name="select-all"
            type="checkbox"
            title=""
            checked={
              isCof
                ? Boolean(
                    cofSelectedDocumentsCount > 0 &&
                    cofSelectedDocumentsCount === documents?.length,
                  )
                : Boolean(
                    selectedDocumentsCounts > 0 &&
                    selectedDocumentsCounts === documents?.length,
                  )
            }
            disabled={!documents?.length}
            onChange={(e) => onGroupSelect(e.target.checked)}
            tabIndex={-1}
          />
          Name
        </div>
        <div className={cn.documentsHeaderRow}>Status</div>
      </div>
      {allowedDocumentsCategories?.map((category) => (
        <DocumentRow
          key={category.id}
          title={category?.name}
          documents={documents?.filter(
            (item) =>
              item.documentCategoryId === category.id ||
              item.category === category?.category,
          )}
          onSelect={(selected, id) => {
            if (isCof) {
              dispatch(onSelectCofDocument({ selected, id }));
            } else if (isCorporate) {
              dispatch(onSelectCorporateDocument({ selected, id }));
            } else {
              dispatch(onSelectDocument({ selected, id }));
            }
          }}
          error={
            ((errors &&
              errors[category.id] &&
              errors[category.id]?.message) as string) ||
            (errors &&
              errors[category?.category as string] &&
              (errors[category?.category as string]?.message as string)) ||
            ''
          }
          categoryId={category.id}
          categoryName={category?.category}
          applicationId={applicationId}
          applicationStatus={applicationStatus}
          clearErrors={clearErrors}
          search={search}
          setRenamePopupVisible={setRenamePopupVisible}
          setSelectedDocument={setSelectedDocument}
          renameAvailable={renameAvailable}
          isCorporate={isCorporate}
          isCof={isCof}
        />
      ))}
      <DocumentsPopups
        selectedDocumentsCounts={selectedDocumentsCounts}
        isCof={isCof}
        isCorporate={isCorporate}
        cofSelectedDocumentsCount={cofSelectedDocumentsCount}
        corporateSelectedDocumentsCount={corporateSelectedDocumentsCount}
        setDeletePopupVisible={setDeletePopupVisible}
        onDeleteDocuments={onDeleteDocuments}
        deleteInProgress={deleteInProgress}
        deletePopupVisible={deletePopupVisible}
        setRenamePopupVisible={setRenamePopupVisible}
        setSelectedDocument={setSelectedDocument}
        setDocumentName={setDocumentName}
        setNameError={setNameError}
        onRename={onRename}
        renameInProgress={renameInProgress}
        renamePopupVisible={renamePopupVisible}
        selectedDocument={selectedDocument}
        documentName={documentName}
        nameError={nameError}
        renameErrors={renameErrors}
      />
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleCloseCreateDocumentSidepanel}
        title="Add new document"
        sidepanelExpandedKey="application-document"
        footer={
          <>
            <ButtonPrimary
              text={documentSubmitInProgress ? 'Loading...' : 'Add document'}
              onClick={onSubmitDocuments}
              className={cn.documentSubmit}
              bold
              tabIndex={-1}
            />
            <ButtonSecondary
              text="Cancel"
              onClick={() => setPanelIsOpen(true)}
              bold
              tabIndex={-1}
            />
          </>
        }
      >
        <CreateApplicationDocument
          uploadInputRef={uploadInputRef}
          dropZoneInputRef={dropZoneInputRef}
          documents={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          uploadedFilesErrors={uploadedFilesErrors}
          selectedGroup={selectedGroup}
          onUpload={onUpload}
          onSelect={setSelectedGroup}
          fileUploadInProgress={
            checkDocumentNameInProgress || fileUploadInProgress
          }
          onDelete={onDelete}
          setExpirationDate={setExpirationDate}
          expirationDate={expirationDate}
          allowedDocumentsCategories={allowedDocumentsCategories}
          documentGroupError={documentGroupError}
          attachedDocumentsErrors={attachedDocumentsErrors}
          attachedFiles={attachedFiles}
          uploadedDocumentsErrors={uploadedDocumentsErrors}
        />
      </SidePanel>
    </StepPartBlock>
  );
};

export default ApplicationDocuments;
